import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { useAuthStore } from '../../stores/authStore.js';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useTrusteesFooter, renderTrusteesHtml } from '../../components/reportTrusteesFooter';

const formatCurrency = (value) => {
  if (!value || isNaN(value)) return 'Rp 0';
  return 'Rp ' + Number(value).toLocaleString('id-ID');
};

const formatBulanTahun = (month, year) => {
  const nama = ['Januari','Februari','Maret','April','Mei','Juni','Juli','Agustus','September','Oktober','November','Desember'];
  return `${nama[month - 1]} ${year}`;
};

function SaldoSimpanan() {
  const { user } = useAuthStore();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [coopProfile, setCoopProfile] = useState(null);
  const [branchInfo, setBranchInfo] = useState(null);
  const [logoBase64, setLogoBase64] = useState('');
  const now = new Date();
  const [bulan, setBulan] = useState(now.getMonth() + 1);
  const [tahun, setTahun] = useState(now.getFullYear());
  const startDate = `${tahun}-${String(bulan).padStart(2, '0')}-01`;
  const endDate = `${tahun}-${String(bulan).padStart(2, '0')}-${new Date(tahun, bulan, 0).getDate()}`;

  const { trustees } = useTrusteesFooter(startDate, endDate);

  useEffect(() => {
    const loadLogo = async () => {
      try {
        const response = await fetch('/logo.jpeg');
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onloadend = () => setLogoBase64(reader.result);
        reader.readAsDataURL(blob);
      } catch (e) { console.log('Failed to load logo') }
    }
    loadLogo();

    const fetchProfileInfo = async () => {
      try {
        const response = await api.get('/reports/profile-info');
        setCoopProfile(response.data?.data?.coopProfile);
        setBranchInfo(response.data?.data?.branchInfo);
      } catch (error) {
        console.error('Error fetching profile info:', error);
      }
    };
    fetchProfileInfo();
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/reports/saldo-simpanan', { params: { startDate, endDate } });
      setData(response.data?.data);
    } catch (error) {
      console.error('Error fetching saldo simpanan:', error);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [bulan, tahun]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePrint = () => {
    if (!data) return;
    const printWindow = window.open('', '_blank');
    const rowsHtml = (data.result || []).map((r, i) => `
      <tr>
        <td class="text-center">${i + 1}</td>
        <td>${r.NBA}</td>
        <td>${r.UUID || '-'}</td>
        <td>${r.Nama}</td>
        <td class="text-right">${formatCurrency(r.TotalSimpananPokok)}</td>
        <td class="text-right">${formatCurrency(r.TotalSimpananWajib)}</td>
        <td class="text-right">${formatCurrency(r.TotalSimpananSukarela)}</td>
        <td class="text-right font-weight-bold">${formatCurrency(r.Total)}</td>
      </tr>
    `).join('');

    const htmlContent = `
      <html>
        <head>
          <title>Cetak Laporan Saldo Simpanan</title>
          <style>
            body { font-family: sans-serif; padding: 30px; font-size: 11px; }
            .kop-surat { text-align: center; border-bottom: 3px solid #000; padding-bottom: 15px; margin-bottom: 20px; }
            .kop-logo { width: 70px; height: auto; }
            .kop-nama { font-size: 20px; font-weight: bold; margin: 5px 0; }
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid #ccc; padding: 5px; font-size: 10px; }
            .text-right { text-align: right; }
            .text-center { text-align: center; }
            .bg-total { background: #333; color: white; font-weight: bold; }
            .bg-subtotal { background: #f2f2f2; font-weight: bold; }
          </style>
        </head>
        <body>
          <div class="kop-surat">
            <img src="${window.location.origin}/logo.jpeg" class="kop-logo" alt="Logo" />
            <div class="kop-nama">${coopProfile?.NamaKoperasi || 'KOPERASI SERBA USAHA'}</div>
            <div>${coopProfile?.Alamat || ''}</div>
          </div>
          <h3 style="text-align:center">LAPORAN SALDO SIMPANAN ANGGOTA</h3>
          <p style="text-align:center">Periode ${formatBulanTahun(bulan, tahun)}</p>
          <table>
            <thead>
              <tr style="background:#e8f4f8"><th>No</th><th>NBA</th><th>UID</th><th>Nama Anggota</th><th class="text-right">Simpanan Pokok</th><th class="text-right">Simpanan Wajib</th><th class="text-right">Simpanan Sukarela</th><th class="text-right">Total</th></tr>
            </thead>
            <tbody>${rowsHtml}</tbody>
            <tfoot>
              <tr class="bg-total"><td colspan="4" class="text-center">TOTAL</td><td class="text-right">${formatCurrency(data.totals.TotalPokok)}</td><td class="text-right">${formatCurrency(data.totals.TotalWajib)}</td><td class="text-right">${formatCurrency(data.totals.TotalSukarela)}</td><td class="text-right">${formatCurrency(data.totals.TotalSemua)}</td></tr>
            </tfoot>
          </table>
          <p style="text-align:right; margin-top: 30px;">Jumlah Anggota: ${data.totals.TotalAnggota}</p>
          ${renderTrusteesHtml(trustees)}
        </body>
      </html>
    `;
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.print();
  };

  const handleExportExcel = () => {
    if (!data) return;
    const wb = XLSX.utils.book_new();
    const wsData = [
      [coopProfile?.NamaKoperasi || 'Koperasi'],
      ['LAPORAN SALDO SIMPANAN ANGGOTA'],
      [`Periode ${formatBulanTahun(bulan, tahun)}`],
      [],
      ['No', 'NBA', 'UID', 'Nama Anggota', 'Simpanan Pokok', 'Simpanan Wajib', 'Simpanan Sukarela', 'Total']
    ];

    (data.result || []).forEach((r, i) => {
      wsData.push([i + 1, r.NBA, r.UUID || '-', r.Nama, r.TotalSimpananPokok, r.TotalSimpananWajib, r.TotalSimpananSukarela, r.Total]);
    });

    wsData.push([]);
    wsData.push(['', '', '', 'TOTAL', data.totals.TotalPokok, data.totals.TotalWajib, data.totals.TotalSukarela, data.totals.TotalSemua]);
    wsData.push(['', '', '', 'Jumlah Anggota', data.totals.TotalAnggota]);

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, 'SaldoSimpanan');
    XLSX.writeFile(wb, 'Saldo_Simpanan_Anggota.xlsx');
  };

  const handleExportPDF = () => {
    if (!data) return;
    const doc = new jsPDF({ orientation: 'l' });
    const pageWidth = doc.internal.pageSize.width;

    try {
      doc.addImage(logoBase64, 'JPEG', 14, 5, 20, 20);
    } catch (e) { console.log('Logo PDF failed:', e.message) }

    doc.setFontSize(16).setFont(undefined, 'bold');
    doc.text(coopProfile?.NamaKoperasi || 'KOPERASI SERBA USAHA', pageWidth / 2, 10, { align: 'center' });
    doc.setFontSize(8).setFont(undefined, 'normal');
    doc.text(coopProfile?.Alamat || '', pageWidth / 2, 16, { align: 'center' });

    doc.setLineWidth(0.5);
    doc.line(10, 20, pageWidth - 10, 20);
    doc.setLineWidth(0.3);
    doc.line(10, 21, pageWidth - 10, 21);

    doc.setFontSize(11).setFont(undefined, 'bold');
    doc.text('LAPORAN SALDO SIMPANAN ANGGOTA', pageWidth / 2, 28, { align: 'center' });
    doc.setFontSize(8).setFont(undefined, 'normal');
    doc.text(`Periode ${formatBulanTahun(bulan, tahun)}`, pageWidth / 2, 33, { align: 'center' });

    const bodyRows = (data.result || []).map((r, i) => [
      i + 1, r.NBA, r.UUID || '-', r.Nama,
      { content: Number(r.TotalSimpananPokok).toLocaleString('id-ID'), styles: { halign: 'right' } },
      { content: Number(r.TotalSimpananWajib).toLocaleString('id-ID'), styles: { halign: 'right' } },
      { content: Number(r.TotalSimpananSukarela).toLocaleString('id-ID'), styles: { halign: 'right' } },
      { content: Number(r.Total).toLocaleString('id-ID'), styles: { halign: 'right', fontStyle: 'bold' } }
    ]);

    autoTable(doc, {
      startY: 38,
      head: [['No', 'NBA', 'UID', 'Nama Anggota', 'Simpanan Pokok', 'Simpanan Wajib', 'Simpanan Sukarela', 'Total']],
      body: [
        ...bodyRows,
        [
          { content: 'TOTAL', colSpan: 4, styles: { halign: 'center', fontStyle: 'bold', fillColor: [51, 51, 51], textColor: 255 } },
          { content: Number(data.totals.TotalPokok).toLocaleString('id-ID'), styles: { halign: 'right', fontStyle: 'bold', fillColor: [51, 51, 51], textColor: 255 } },
          { content: Number(data.totals.TotalWajib).toLocaleString('id-ID'), styles: { halign: 'right', fontStyle: 'bold', fillColor: [51, 51, 51], textColor: 255 } },
          { content: Number(data.totals.TotalSukarela).toLocaleString('id-ID'), styles: { halign: 'right', fontStyle: 'bold', fillColor: [51, 51, 51], textColor: 255 } },
          { content: Number(data.totals.TotalSemua).toLocaleString('id-ID'), styles: { halign: 'right', fontStyle: 'bold', fillColor: [51, 51, 51], textColor: 255 } }
        ]
      ],
      theme: 'grid',
      styles: { fontSize: 7 },
      headStyles: { fillColor: [232, 244, 248], textColor: [0, 0, 0], fontStyle: 'bold' }
    });

    const footerY = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(8);
    doc.text(`Jumlah Anggota: ${data.totals.TotalAnggota}`, 14, footerY);

    if (trustees.length > 0) {
      doc.setFontSize(6);
      const midX = pageWidth / 2;
      
      const pengurus = trustees.filter(t => t.Kelompok === 'Pengurus');
      const pengawas = trustees.filter(t => t.Kelompok === 'Pengawas');
      const pengurusSub = pengurus.filter(t => t.Jabatan === 'Anggota/Panitia Kredit');
      const pengurusRest = pengurus.filter(t => t.Jabatan !== 'Anggota/Panitia Kredit');

      let yL = footerY + 8;
      if (pengurus.length) {
        doc.setFont(undefined, 'bold');
        doc.text('PENGURUS:', 14, yL);
        yL += 3;
        doc.setFont(undefined, 'normal');
        pengurusRest.forEach(t => {
          doc.text(`${t.Jabatan} : ${t.NamaLengkap}`, 19, yL);
          yL += 3;
        });
        if (pengurusSub.length) {
          doc.text('Anggota/Panitia Kredit :', 19, yL);
          yL += 3;
          pengurusSub.forEach((t, i) => {
            doc.text(`${i + 1}. ${t.NamaLengkap}`, 24, yL);
            yL += 3;
          });
        }
      }

      let yR = footerY + 8;
      if (pengawas.length) {
        doc.setFont(undefined, 'bold');
        doc.text('PENGAWAS:', midX + 5, yR);
        yR += 3;
        doc.setFont(undefined, 'normal');
        pengawas.forEach(t => {
          doc.text(`${t.Jabatan} : ${t.NamaLengkap}`, midX + 10, yR);
          yR += 3;
        });
      }
    }

    doc.save('Saldo_Simpanan_Anggota.pdf');
  };

  return (
    <div className="container-fluid p-3">
      <div className="card shadow border-0">
        <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
          <h5 className="m-0 font-weight-bold">Saldo Simpanan Anggota</h5>
          <div className="btn-group">
            <button className="btn btn-sm btn-success" onClick={handleExportExcel} disabled={loading}><i className="fas fa-file-excel mr-1"></i> Excel</button>
            <button className="btn btn-sm btn-danger" onClick={handleExportPDF} disabled={loading}><i className="fas fa-file-pdf mr-1"></i> PDF</button>
            <button className="btn btn-sm btn-light" onClick={handlePrint} disabled={loading}><i className="fas fa-print mr-1"></i> Cetak</button>
          </div>
        </div>

        <div className="card-body">
          <div className="row mb-4 bg-light p-3 rounded align-items-end">
            <div className="col-md-2">
              <label className="small font-weight-bold">BULAN</label>
              <select className="form-control" value={bulan} onChange={(e) => setBulan(Number(e.target.value))}>
                {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                  <option key={m} value={m}>{['Januari','Februari','Maret','April','Mei','Juni','Juli','Agustus','September','Oktober','November','Desember'][m-1]}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="small font-weight-bold">TAHUN</label>
              <select className="form-control" value={tahun} onChange={(e) => setTahun(Number(e.target.value))}>
                {[2026, 2027, 2028, 2029, 2030].map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          </div>

          {loading ? (
            <div className="text-center my-5"><div className="spinner-border text-primary"></div></div>
          ) : !data ? (
            <div className="alert alert-info">Data tidak tersedia.</div>
          ) : (
            <>
              <div className="alert alert-secondary d-flex justify-content-between">
                <span>Total Anggota: <b>{data.totals.TotalAnggota}</b></span>
                <span>Total Pokok: <b>{formatCurrency(data.totals.TotalPokok)}</b></span>
                <span>Total Wajib: <b>{formatCurrency(data.totals.TotalWajib)}</b></span>
                <span>Total Sukarela: <b>{formatCurrency(data.totals.TotalSukarela)}</b></span>
                <span>Total Semua: <b className="text-primary">{formatCurrency(data.totals.TotalSemua)}</b></span>
              </div>

              <div className="table-responsive">
                <table className="table table-sm table-bordered table-striped">
                  <thead className="thead-light">
                    <tr>
                      <th className="text-center" style={{ width: 40 }}>No</th>
                      <th>NBA</th>
                      <th>UID</th>
                      <th>Nama Anggota</th>
                      <th className="text-right">Simpanan Pokok</th>
                      <th className="text-right">Simpanan Wajib</th>
                      <th className="text-right">Simpanan Sukarela</th>
                      <th className="text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.result || []).map((r, i) => (
                      <tr key={r.NBA}>
                        <td className="text-center">{i + 1}</td>
                        <td>{r.NBA}</td>
                        <td>{r.UUID || '-'}</td>
                        <td>{r.Nama}</td>
                        <td className="text-right">{formatCurrency(r.TotalSimpananPokok)}</td>
                        <td className="text-right">{formatCurrency(r.TotalSimpananWajib)}</td>
                        <td className="text-right">{formatCurrency(r.TotalSimpananSukarela)}</td>
                        <td className="text-right font-weight-bold">{formatCurrency(r.Total)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="table-dark font-weight-bold">
                      <td colSpan="4" className="text-center">TOTAL</td>
                      <td className="text-right">{formatCurrency(data.totals.TotalPokok)}</td>
                      <td className="text-right">{formatCurrency(data.totals.TotalWajib)}</td>
                      <td className="text-right">{formatCurrency(data.totals.TotalSukarela)}</td>
                      <td className="text-right text-warning">{formatCurrency(data.totals.TotalSemua)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default SaldoSimpanan;
